// externals
import type { SyntheticEvent } from 'react';

// libraries
import type { KeyboardEvent } from 'react';

import { Badge } from '@interstate/components/Badge';
import type { BadgeVariant } from '@interstate/components/Badge';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { formatAPR, formatDollarsAndCents, formatNumber, getNumberOfDaysStartingFromTargetDate } from '@makemydeal/dr-common-utils';

// hooks
import { useAiAltVehicleOptionsCardActions } from './useAiAltVehicleOptions';

// types
import type { AiAltVehicleOption } from '../../types/AiAltVehicleOption';

// assets
import VehiclePlaceholderSvg from '../../assets/blankVehicle.svg';

// styles
import {
    AiAltVehicleOptionsCardActions,
    AiAltVehicleOptionsCardContainer,
    AiAltVehicleOptionsCardDetails,
    AiAltVehicleOptionsCardDivider,
    AiAltVehicleOptionsCardImage,
    AiAltVehicleOptionsCardMetric,
    AiAltVehicleOptionsCardMetricsList,
    AiAltVehicleOptionsCardAcceptanceMetric,
    AiAltVehicleOptionsCardSummaryMetric,
    AiAltVehicleOptionsCardVehicleDetails,
    AiAltVehicleOptionsCardVehicleInfo
} from './AiAltVehicleOptionsSection.style';

// Ported from the Manager View AiVehicleRecommendationSlideout per-vehicle card so the moved card
// keeps the same fields, formatting, and layout. `activities-change-vehicle` cannot import it
// (circular dep with `activities-desking`, AD-1), so these helpers are copied verbatim.
const formatVehicleAge = (inventoryDate?: string): string => {
    const days = inventoryDate ? getNumberOfDaysStartingFromTargetDate(inventoryDate) : undefined;
    if (days === undefined) return 'N/A';
    return `${days} day${days === 1 ? '' : 's'}`;
};

const formatMoneyFactor = (moneyFactor: number): string => moneyFactor.toFixed(5);

const getAffordabilityStatus = (level: string): BadgeVariant => (level.toUpperCase() === 'HIGH' ? 'success' : 'light');

const normalizeAffordabilityLevel = (level: string): string => level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();

export type AiAltVehicleOptionsCardProps = {
    recommendation: AiAltVehicleOption;
    isSelected: boolean;
    onSelect: () => void;
    'data-testid'?: string;
};

/**
 * A single AI-recommended vehicle card. Its displayed fields are all props; only the inline-action state below comes
 * from the store, read here rather than threaded through the section so every host renders the same actions.
 * Same fields as the Manager View slideout's per-vehicle card (image, year/make/model/trim, color +
 * mileage, Monthly Payment / Selling Price / Down Payment / APR / Age, Profit Potential, and an
 * acceptance-likelihood Badge). Desktop layout (Figma 9013-15738 / 9013-15682) stacks the vehicle
 * image above the title/subtitle; below `BreakPoint.MEDIUM` the styles (AiAltVehicleOptionsSection.
 * style) switch to the tablet spec (Figma 9061-12257) — image beside the details, metrics as
 * side-by-side columns, and a divider above the footer.
 * The selected card also carries the inline "Select Vehicle" / "New Scenario" pair the result rows show, gated on
 * AVO being active.
 */
const AiAltVehicleOptionsCard = ({
    recommendation,
    isSelected,
    onSelect,
    'data-testid': dataTestId = 'ai-alt-vehicle-options-card'
}: AiAltVehicleOptionsCardProps) => {
    const { vehicle, payment, affordability } = recommendation;
    const {
        isAiAltVehicleOptionsActive,
        isInlineActionsDisabled,
        isChangeProcessing,
        canAddNewScenario,
        isAddNewScenarioDisabled,
        handleSelectVehicleClick,
        handleAddNewScenarioClick
    } = useAiAltVehicleOptionsCardActions();

    // Inline actions belong to the selected card only — the same rule the result rows follow.
    const shouldRenderInlineActions = isAiAltVehicleOptionsActive && isSelected;

    // Enter and Space select the card, matching what a native button would do. Guarded to the card itself so a key
    // press on a control inside it cannot bubble up and re-select.
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }

        // Space scrolls the page otherwise.
        event.preventDefault();
        onSelect();
    };

    return (
        <AiAltVehicleOptionsCardContainer
            isSelected={isSelected}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
            data-testid={dataTestId}
        >
            <AiAltVehicleOptionsCardVehicleInfo>
                <AiAltVehicleOptionsCardImage src={vehicle.imageUrl || VehiclePlaceholderSvg} alt="Vehicle Image" />
                <AiAltVehicleOptionsCardDetails>
                    <AiAltVehicleOptionsCardVehicleDetails>
                        <Typography variant="h5" data-testid="ai-alt-vehicle-options-card-title">
                            {`${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`}
                        </Typography>
                        <Typography
                            variant="body-sm"
                            color="sem.color.on-surface.subdued"
                            data-testid="ai-alt-vehicle-options-card-subtitle"
                        >
                            {[vehicle.exteriorColor, `${formatNumber(vehicle.odometer ?? 0)} miles`].filter(Boolean).join(' | ')}
                        </Typography>
                    </AiAltVehicleOptionsCardVehicleDetails>
                    <AiAltVehicleOptionsCardMetricsList>
                        <AiAltVehicleOptionsCardMetric>
                            <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                Monthly Payment
                            </Typography>
                            <Typography variant="body-sm" data-testid="ai-alt-vehicle-options-card-monthly">
                                {`${formatDollarsAndCents(payment.monthlyPayment)}/mo`}
                            </Typography>
                        </AiAltVehicleOptionsCardMetric>
                        <AiAltVehicleOptionsCardMetric>
                            <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                Down Payment
                            </Typography>
                            <Typography variant="body-sm" data-testid="ai-alt-vehicle-options-card-down-payment">
                                {formatDollarsAndCents(payment.downPayment)}
                            </Typography>
                        </AiAltVehicleOptionsCardMetric>
                        <AiAltVehicleOptionsCardMetric>
                            <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                Selling Price
                            </Typography>
                            <Typography variant="body-sm" data-testid="ai-alt-vehicle-options-card-selling-price">
                                {formatDollarsAndCents(vehicle.listedPrice)}
                            </Typography>
                        </AiAltVehicleOptionsCardMetric>
                        <AiAltVehicleOptionsCardMetric>
                            <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                Age
                            </Typography>
                            <Typography variant="body-sm" data-testid="ai-alt-vehicle-options-card-age">
                                {formatVehicleAge(vehicle.inventoryDate)}
                            </Typography>
                        </AiAltVehicleOptionsCardMetric>
                        {payment.moneyFactor != null ? (
                            <AiAltVehicleOptionsCardMetric>
                                <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                    Money Factor
                                </Typography>
                                <Typography variant="body-sm" data-testid="ai-alt-vehicle-options-card-money-factor">
                                    {formatMoneyFactor(payment.moneyFactor)}
                                </Typography>
                            </AiAltVehicleOptionsCardMetric>
                        ) : (
                            <AiAltVehicleOptionsCardMetric>
                                <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                    APR
                                </Typography>
                                <Typography variant="body-sm" data-testid="ai-alt-vehicle-options-card-apr">{`${formatAPR(
                                    payment.sellRate ?? payment.apr
                                )}%`}</Typography>
                            </AiAltVehicleOptionsCardMetric>
                        )}
                        <AiAltVehicleOptionsCardDivider />
                        {payment.vehicleProfit !== undefined && (
                            <AiAltVehicleOptionsCardSummaryMetric>
                                <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                    Profit Potential
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="sem.color.on-surface.success.default"
                                    data-testid="ai-alt-vehicle-options-card-profit"
                                >
                                    {`+${formatDollarsAndCents(payment.vehicleProfit)}`}
                                </Typography>
                            </AiAltVehicleOptionsCardSummaryMetric>
                        )}
                        <AiAltVehicleOptionsCardAcceptanceMetric>
                            <Typography variant="label-sm" color="sem.color.on-surface.muted">
                                Acceptance Likelihood
                            </Typography>
                            <Badge
                                variant={getAffordabilityStatus(affordability.level)}
                                data-testid="ai-alt-vehicle-options-card-acceptance"
                            >
                                {normalizeAffordabilityLevel(affordability.level)}
                            </Badge>
                        </AiAltVehicleOptionsCardAcceptanceMetric>
                    </AiAltVehicleOptionsCardMetricsList>
                    {shouldRenderInlineActions && (
                        <AiAltVehicleOptionsCardActions>
                            <Button
                                buttonStyle="primary"
                                size="aux"
                                data-testid={`${dataTestId}-select-vehicle`}
                                disabled={isInlineActionsDisabled}
                                isLoading={isChangeProcessing}
                                onClick={(event: SyntheticEvent) => {
                                    // The card's own onClick would redundantly re-set the same selectedVehicle.
                                    event.stopPropagation();
                                    handleSelectVehicleClick();
                                }}
                            >
                                Select Vehicle
                            </Button>
                            {/* Only hosts that can create a scenario offer one, which today is Manager View alone. */}
                            {canAddNewScenario && (
                                <Button
                                    buttonStyle="secondary"
                                    size="aux"
                                    data-testid={`${dataTestId}-new-scenario`}
                                    disabled={isChangeProcessing || isAddNewScenarioDisabled}
                                    onClick={(event: SyntheticEvent) => {
                                        event.stopPropagation();
                                        handleAddNewScenarioClick();
                                    }}
                                >
                                    New Scenario
                                </Button>
                            )}
                        </AiAltVehicleOptionsCardActions>
                    )}
                </AiAltVehicleOptionsCardDetails>
            </AiAltVehicleOptionsCardVehicleInfo>
        </AiAltVehicleOptionsCardContainer>
    );
};

export default AiAltVehicleOptionsCard;
